import { Link } from "react-router";
import { ArrowLeft, BookOpen, Database, Cpu, TestTube, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Main } from "@/components/shared/Main";
import { Header } from "@/components/shared/Header";

export function GlossaryDocPage() {
  return (
    <>
      <Header />
      <Main>
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/docs" className="flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>

          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mr-4">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Glossary</h1>
              <p className="text-xl text-gray-600">Key terms and concepts</p>
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Table of Contents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <Database className="w-4 h-4 mr-2" />
                  Data & Files
                </h4>
                <ul className="text-sm space-y-1">
                  <li>
                    <a href="#specs-excel" className="text-blue-600 hover:underline">
                      Specs Excel
                    </a>
                  </li>
                  <li>
                    <a href="#pcm-overview" className="text-blue-600 hover:underline">
                      PCM Overview
                    </a>
                  </li>
                  <li>
                    <a href="#chip-pos-files" className="text-blue-600 hover:underline">
                      Chip Position Files
                    </a>
                  </li>
                  <li>
                    <a href="#masks-csv" className="text-blue-600 hover:underline">
                      Masks CSV
                    </a>
                  </li>
                  <li>
                    <a href="#xml-files" className="text-blue-600 hover:underline">
                      XML Files
                    </a>
                  </li>
                  <li>
                    <a href="#exported-specs" className="text-blue-600 hover:underline">
                      Exported Specs
                    </a>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <Cpu className="w-4 h-4 mr-2" />
                  Components & Testing
                </h4>
                <ul className="text-sm space-y-1">
                  <li>
                    <a href="#mask" className="text-blue-600 hover:underline">
                      Mask
                    </a>
                  </li>
                  <li>
                    <a href="#structure" className="text-blue-600 hover:underline">
                      Structure
                    </a>
                  </li>
                  <li>
                    <a href="#test-site" className="text-blue-600 hover:underline">
                      Test Site
                    </a>
                  </li>
                  <li>
                    <a href="#test-label" className="text-blue-600 hover:underline">
                      Test Label
                    </a>
                  </li>
                  <li>
                    <a href="#pcm" className="text-blue-600 hover:underline">
                      PCM
                    </a>
                  </li>
                  <li>
                    <a href="#component-class" className="text-blue-600 hover:underline">
                      Component Class
                    </a>
                  </li>
                  <li>
                    <a href="#spec" className="text-blue-600 hover:underline">
                      Spec
                    </a>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <TestTube className="w-4 h-4 mr-2" />
                  Measurement & Analysis
                </h4>
                <ul className="text-sm space-y-1">
                  <li>
                    <a href="#measurement-setup" className="text-blue-600 hover:underline">
                      Measurement Setup
                    </a>
                  </li>
                  <li>
                    <a href="#measurement-recipe" className="text-blue-600 hover:underline">
                      Measurement Recipe
                    </a>
                  </li>
                  <li>
                    <a href="#test-request" className="text-blue-600 hover:underline">
                      Test Request
                    </a>
                  </li>
                  <li>
                    <a href="#device-parameter" className="text-blue-600 hover:underline">
                      Device Parameter
                    </a>
                  </li>
                  <li>
                    <a href="#pholis" className="text-blue-600 hover:underline">
                      Pholis
                    </a>
                  </li>
                  <li>
                    <a href="#wafer-testers" className="text-blue-600 hover:underline">
                      Wafer Testers
                    </a>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  Roles & Tools
                </h4>
                <ul className="text-sm space-y-1">
                  <li>
                    <a href="#designer" className="text-blue-600 hover:underline">
                      Designer
                    </a>
                  </li>
                  <li>
                    <a href="#project-manager" className="text-blue-600 hover:underline">
                      Project Manager
                    </a>
                  </li>
                  <li>
                    <a href="#test-engineer" className="text-blue-600 hover:underline">
                      Test Engineer
                    </a>
                  </li>
                  <li>
                    <a href="#pdk-team" className="text-blue-600 hover:underline">
                      PDK Team
                    </a>
                  </li>
                  <li>
                    <a href="#customer" className="text-blue-600 hover:underline">
                      Customer
                    </a>
                  </li>
                  <li>
                    <a href="#metromap" className="text-blue-600 hover:underline">
                      Metromap
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data & Files Section */}
        <Card className="mb-8" id="data-files">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Database className="w-5 h-5 mr-2" />
              Data & Files
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div id="specs-excel">
              <h3 className="font-semibold text-gray-900 mb-2">Specs Excel</h3>
              <p className="text-gray-700 text-sm mb-2">
                Master specification file (ISIPP200_specs.xlsx) hosted on SharePoint and maintained
                by the PCM design team. This file contains all the standard specifications that are
                applied during data analysis.
              </p>
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-blue-800 text-xs">
                  <strong>Note:</strong> Only PCM design team members can modify this file.
                  Project-specific overrides are stored separately and managed by project managers.
                </p>
              </div>
            </div>

            <div id="pcm-overview">
              <h3 className="font-semibold text-gray-900 mb-2">PCM Overview</h3>
              <p className="text-gray-700 text-sm">
                Maintained by PCM designers and photonics PDK team, this file tracks the
                relationship between taped-out masks and their corresponding PCM versions.
              </p>
            </div>

            <div id="chip-pos-files">
              <h3 className="font-semibold text-gray-900 mb-2">Chip Position Files</h3>
              <p className="text-gray-700 text-sm">
                Files that define wafer maps based on pline reports for specific mask sets. These
                files specify the exact positioning and layout of chips on wafers for testing
                purposes.
              </p>
            </div>

            <div id="masks-csv">
              <h3 className="font-semibold text-gray-900 mb-2">Masks CSV</h3>
              <p className="text-gray-700 text-sm">
                Contains information about masks used to create structures on wafers, including mask
                set details, wafer size, die size, printing offset, and PCM version information.
              </p>
            </div>

            <div id="xml-files">
              <h3 className="font-semibold text-gray-900 mb-2">XML Files</h3>
              <p className="text-gray-700 text-sm">
                Design files containing mask layout information, design parameters, and measurement
                instructions. These files include coordinates for optical grating couplers,
                electrical probe pads, and "port combos" that define how to measure specific
                components.
              </p>
            </div>

            <div id="exported-specs">
              <h3 className="font-semibold text-gray-900 mb-2">Exported Specs CSV</h3>
              <p className="text-gray-700 text-sm">
                Defines which specifications can be exported to wafer and lot trendchart files. Acts
                as a filter for what appears in analysis reports.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Components & Testing Section */}
        <Card className="mb-8" id="components-testing">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Cpu className="w-5 h-5 mr-2" />
              Components & Testing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div id="mask">
              <h3 className="font-semibold text-gray-900 mb-2">Mask</h3>
              <p className="text-gray-700 text-sm">
                Patterns used to create structures on wafers, containing design information.
                Includes both customer payload and reserved space for PCM structures for process
                monitoring.
              </p>
            </div>

            <div id="structure">
              <h3 className="font-semibold text-gray-900 mb-2">Structure</h3>
              <p className="text-gray-700 text-sm">
                Physical structures created on wafers using mask design information. Can be complex
                circuits or individual components, typically compared against reference waveguides
                for performance evaluation.
              </p>
            </div>

            <div id="test-site">
              <h3 className="font-semibold text-gray-900 mb-2">Test Site</h3>
              <p className="text-gray-700 text-sm">
                A unit cell on a mask that groups multiple structures, possibly with reference
                waveguides. Measuring test sites allows calculation of device parameters that are
                compared against specifications.
              </p>
            </div>

            <div id="test-label">
              <h3 className="font-semibold text-gray-900 mb-2">Test Label</h3>
              <p className="text-gray-700 text-sm">
                Bundles multiple test sites together. When added to a measurement request, all
                contained test sites will be measured. Test sites can appear in multiple test labels
                for flexible organization.
              </p>
            </div>

            <div id="pcm">
              <h3 className="font-semibold text-gray-900 mb-2">PCM (Process Control Monitor)</h3>
              <p className="text-gray-700 text-sm">
                Reference devices printed on mask sets to monitor wafer fabrication processes. Each
                mask set includes a specific PCM version with defined specifications for normal
                device operation.
              </p>
            </div>

            <div id="component-class">
              <h3 className="font-semibold text-gray-900 mb-2">Component Class</h3>
              <p className="text-gray-700 text-sm">
                Classification system for test sites and structures that determines applicable
                measurement recipes, analysis methods, and specifications. Essential for organizing
                testing workflows.
              </p>
            </div>

            <div id="spec">
              <h3 className="font-semibold text-gray-900 mb-2">Spec</h3>
              <p className="text-gray-700 text-sm">
                Defines expected nominal operating points for functional devices, including full
                name, mnemonic, target value, limits, and associated structure name. Organized by
                component class.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Measurement & Analysis Section */}
        <Card className="mb-8" id="measurement-analysis">
          <CardHeader>
            <CardTitle className="flex items-center">
              <TestTube className="w-5 h-5 mr-2" />
              Measurement & Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div id="measurement-setup">
              <h3 className="font-semibold text-gray-900 mb-2">Measurement Setup</h3>
              <p className="text-gray-700 text-sm mb-3">
                Automated assemblies of test instruments for characterizing test sites on wafers.
                Each setup has specific capabilities and configurations.
              </p>
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">Available Setups:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-blue-800 text-xs">
                  <div>
                    • <strong>NYX:</strong> Lab, WAFT, Fully automatic, 200/300mm
                  </div>
                  <div>
                    • <strong>THOR:</strong> IMEC fab, Pigtail, Fully automatic, 200/300mm
                  </div>
                  <div>
                    • <strong>SILTHOR:</strong> Silterra, Pigtail, Fully automatic, 200mm
                  </div>
                  <div>
                    • <strong>CYCLOPS:</strong> Silterra, Pigtail, Fully automatic, 200/300mm
                  </div>
                  <div>
                    • <strong>OIO200:</strong> Lab, Pigtail, Semi-automatic, 200mm
                  </div>
                  <div>
                    • <strong>OIO300:</strong> Lab, Pigtail, Semi-automatic, 200/300mm
                  </div>
                </div>
              </div>
            </div>

            <div id="measurement-recipe">
              <h3 className="font-semibold text-gray-900 mb-2">Measurement Recipe</h3>
              <p className="text-gray-700 text-sm">
                Specific instructions for measuring structures, applicable to certain component
                classes. Determines which measurement procedures can be used for different types of
                test sites.
              </p>
            </div>

            <div id="test-request">
              <h3 className="font-semibold text-gray-900 mb-2">Test Request</h3>
              <p className="text-gray-700 text-sm">
                Complete measurement job definition including lot number, wafer numbers, test labels
                for each wafer, and wafer maps to be used. Submitted to measurement setups for
                execution.
              </p>
            </div>

            <div id="device-parameter">
              <h3 className="font-semibold text-gray-900 mb-2">Device Parameter</h3>
              <p className="text-gray-700 text-sm">
                Calculated quantities related to the physics of structure operation. Based on
                physical models or simulations, with target values set by designers and compared
                against specifications.
              </p>
            </div>

            <div id="pholis">
              <h3 className="font-semibold text-gray-900 mb-2">Pholis</h3>
              <p className="text-gray-700 text-sm">
                IMEC's Python package for analyzing super raw measurement data from measurement
                setups. Produces evaluated specs in trendchart (CSV) and report (HTML) formats for
                customers.
              </p>
            </div>

            <div id="wafer-testers">
              <h3 className="font-semibold text-gray-900 mb-2">Wafer Testers</h3>
              <p className="text-gray-700 text-sm">
                Python package used to control measurement execution on automated measurement
                setups. Manages test execution workflows and coordinates instrument operations.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Roles & Tools Section */}
        <Card className="mb-8" id="roles-tools">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Roles & Tools
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div id="designer">
              <h3 className="font-semibold text-gray-900 mb-2">Designer</h3>
              <p className="text-gray-700 text-sm">
                Creates mask layouts and populates them with test structures. Responsible for
                structure selection and design parameters, which are mask-specific.
              </p>
            </div>

            <div id="project-manager">
              <h3 className="font-semibold text-gray-900 mb-2">Project Manager</h3>
              <p className="text-gray-700 text-sm">
                Interface between customers and IMEC teams. Manages project deliverables, agrees on
                spec limits with customers, and coordinates between designers and test engineers.
              </p>
            </div>

            <div id="test-engineer">
              <h3 className="font-semibold text-gray-900 mb-2">Test Engineer</h3>
              <p className="text-gray-700 text-sm">
                Executes requested measurements on laboratory or fab measurement setups. Operates
                equipment and ensures quality of measurement data.
              </p>
            </div>

            <div id="pdk-team">
              <h3 className="font-semibold text-gray-900 mb-2">PDK Team</h3>
              <p className="text-gray-700 text-sm">
                Maintains the official component library and provides design toolkits. Defines
                component specs for standard library components based on historical test data.
              </p>
            </div>

            <div id="customer">
              <h3 className="font-semibold text-gray-900 mb-2">Customer</h3>
              <p className="text-gray-700 text-sm">
                Third party for whom silicon photonics wafers are manufactured. Can be external
                companies (bilateral projects) or IMEC ICLink (multi-project wafers/MPW).
              </p>
            </div>

            <div id="metromap">
              <h3 className="font-semibold text-gray-900 mb-2">Metromap</h3>
              <p className="text-gray-700 text-sm">
                IMEC tool with GUI interface for selecting and generating chip position files and
                other measurement-related files. Provides visual editing capabilities for wafer
                maps.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-center">
          <Button asChild variant="outline">
            <Link to="/docs">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>
        </div>
      </Main>
    </>
  );
}
